import { Option, QuestionDto } from '@smatchy/sdk';
import type { AnswerValue } from './what-an-answer-holds';

export class QuestionEntity {
  private _currentOptions: Option[];
  private _currentQuestion: QuestionDto;
  private _historicQuestions: QuestionDto[];
  private _haveNextQuestion = false;
  private _currentAnswerValue: string | number;
  private _historicAnswers: Map<string, { questionId: string; value: AnswerValue }> = new Map<
    string,
    {
      questionId: string;
      value: AnswerValue;
    }
  >();
  private _level = 0;
  private _surveyEnded = false;

  private constructor(private props: QuestionDto) {
    this._currentOptions = props.options;
    this._historicQuestions = [props];
    this._currentQuestion = props;
  }

  get type(): QuestionDto.TypeEnum {
    return this.props.type;
  }

  /** Optional on the generated question: not every question scores against a reference. */
  get scoreRefer(): string | undefined {
    return this._currentQuestion.scoreReferId;
  }

  static create(props: QuestionDto) {
    return new QuestionEntity(props);
  }

  getOptions() {
    return this.props.options;
  }

  getFormattedIdOfQuestion(question: QuestionDto) {
    return `${question.title.toLowerCase()}-${question.type}`;
  }

  getHistoricAnswers() {
    return this._historicAnswers;
  }

  answer(value: string | number, question?: QuestionDto) {
    this._currentAnswerValue = value;
    const questionId = this.getFormattedIdOfQuestion(question || this._currentQuestion);
    this._historicAnswers.set(questionId, { questionId, value });
    const option = this._currentOptions.find((o) => o.value.toString() === value);
    if (option) {
      if (option.scoreLevel) {
        this._level = option.scoreLevel;
      }
      if (option.nested) {
        this._currentOptions = option.nested.options;
        this._currentQuestion = option.nested;
        this._historicQuestions.push(option.nested);
        this._haveNextQuestion = true;
      } else {
        this._haveNextQuestion = false;
        if (this.isNotAutoValidationQuestionType()) {
          this._surveyEnded = true;
        }
      }
    } else {
      this._haveNextQuestion = false;
      if (this.isNotAutoValidationQuestionType()) {
        this._surveyEnded = true;
      }
    }
    return this;
  }

  haveNextQuestion(): boolean {
    return this._haveNextQuestion;
  }

  haveFinished(): boolean {
    return this.isLastQuestion() && !this.isFirstQuestion() && this._surveyEnded;
  }

  haveScoreReferer(): boolean {
    return !this.props.scoreReferId;
  }

  getLevel(): number {
    return this._level;
  }

  getCurrentAnswer() {
    return this._currentAnswerValue;
  }

  getCurrentAnswerOfQuestion() {
    if (this._currentOptions?.find((o) => o.value.toString() === this._currentAnswerValue?.toString())) {
      return this._currentAnswerValue;
    }
    return undefined;
  }

  getAnswerOfQuestion(question: QuestionDto) {
    const answer = this._historicAnswers.get(this.getFormattedIdOfQuestion(question));
    return answer?.value || undefined;
  }

  getCurrentQuestion() {
    return this._currentQuestion;
  }

  isFirstQuestion() {
    return this._historicQuestions[0] == this._currentQuestion;
  }

  getFirstQuestion() {
    return this._historicQuestions[0];
  }

  getLastAnswer() {
    if (this._historicQuestions.length >= 2) {
      const key = Array.from(this._historicAnswers.keys())[this._historicAnswers.size - 2];
      return this._historicAnswers.get(key)?.value;
      // return this._historicAnswers[this._historicAnswers.size - 2]?.value;
    }
  }

  isLastQuestion() {
    return !this.haveNextQuestion();
  }

  validLastQuestion() {
    if (
      this.getCurrentQuestion().type === 'choiceAnswer' ||
      this.getCurrentQuestion().type === 'number' ||
      this.getCurrentQuestion().type === 'float' ||
      this.getCurrentQuestion().type === 'multi'
    ) {
      this._surveyEnded = true;
    }
  }

  private isNotAutoValidationQuestionType() {
    return (
      this._currentQuestion.type !== 'choiceAnswer' &&
      this._currentQuestion.type !== 'number' &&
      this._currentQuestion.type !== 'multi'
    );
  }
}
